import React, { useState } from "react";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import { useNavigate, useLocation, matchPath } from "react-router-dom";
import assets from "../../../assets";
import HeaderStyles from "../../../styles/PublicPage/header";
import { PATH_NAME } from "../../../configs/pathName";
import CustomSVG from "../../../helpers/ButtomSVG/CustomSVG";
import ResponsiveHeader from "./ResponsiveHeader";
import SubmenuHeader from "./SubMenuHeader";
import { Typography } from "@mui/material";
import { typo } from "../../../assets/fonts/font";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import Badge from "@mui/material/Badge";
import colorConfigs from "../../../configs/colorConfigs";
import { useCart } from "../../../context/CartContext";

interface MainHeaderProps {
  appBarHeight: string;
  isMobile: boolean;
  isTablet: boolean;
  basicUserInfo: any;
}

const MainHeader: React.FC<MainHeaderProps> = ({
  appBarHeight,
  isMobile,
  isTablet,
  basicUserInfo,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const isCartEnabled = process.env.REACT_APP_ENABLE_CART === 'true';
  
  const [isSubmenuVisible, setSubmenuVisible] = useState(false);
  let submenuTimeout: ReturnType<typeof setTimeout>;

  const handleNavigation = (path: string) => () => {
    navigate(path);
  };

  const handleLogoClick = () => {
    navigate(PATH_NAME.ROOT);
  };

  const isActive = (path: string) => {
    return !!matchPath(path, location.pathname);
  };

  const showSubmenu = () => {
    clearTimeout(submenuTimeout); // Clear timeout to prevent premature hiding
    setSubmenuVisible(true);
  };

  const hideSubmenu = () => {
    submenuTimeout = setTimeout(() => {
      setSubmenuVisible(false);
    }, 200); // Delay hiding slightly to allow smoother transitions
  };

  const { totalQuantity } = useCart();

  return (
    <Box sx={{ position: "sticky", top: 0, zIndex: 10 }}>
      <AppBar position="static" sx={HeaderStyles.appBar}>
        <Toolbar
          sx={{
            height: appBarHeight,
            justifyContent: "center",
          }}
        >
          <Box
            sx={{
              maxWidth: "1200px",
              width: "100%",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              position: "relative",
            }}
          >
            {isMobile || isTablet ? (
              <ResponsiveHeader isMobile={isMobile} />
            ) : (
              <>
                {/* Left Section */}
                <Box
                  sx={{
                    ...HeaderStyles.flexBox,
                    ...HeaderStyles.flex40,
                    gap: "1rem",
                  }}
                >
                  <Box
                    onMouseEnter={showSubmenu}
                    onMouseLeave={hideSubmenu}
                    sx={{ position: "relative" }}
                  >
                    <Button
                      color="inherit"
                      onClick={handleNavigation(PATH_NAME.ALLPRODUCTS)}
                      sx={{
                        ...HeaderStyles.button,
                        ...(isActive(PATH_NAME.ALLPRODUCTS) || isActive(PATH_NAME.COLLECTIONS)
                        ? HeaderStyles.getActiveButtonStyle(true)
                        : {}),
                      }}
                    >
                      <Typography sx={{ ...typo.navigation }}>
                        All Products
                      </Typography>
                      <CustomSVG />
                    </Button>
                  </Box>

                  <Button
                    color="inherit"
                    onClick={handleNavigation(PATH_NAME.LOCATIONS)}
                    sx={{
                      ...HeaderStyles.button,
                      ...HeaderStyles.getActiveButtonStyle(isActive(PATH_NAME.LOCATIONS)),
                    }}
                  >
                    <Typography sx={{ ...typo.navigation }}>
                      Locations
                    </Typography>
                    <CustomSVG />
                  </Button>
                </Box>

                {/* Center Section */}
                <Box sx={{ ...HeaderStyles.flexBox, ...HeaderStyles.flex20 }}>
                  <Button
                    onClick={handleLogoClick}
                    sx={HeaderStyles.logoButton}
                  >
                    <img
                      src={assets.images.logo}
                      alt="Logo"
                      style={HeaderStyles.logo}
                    />
                  </Button>
                </Box>

                {/* Right Section */}
                <Box
                  sx={{
                    ...HeaderStyles.flexBox,
                    ...HeaderStyles.flex40,
                    ...HeaderStyles.flexEnd,
                    gap: "1rem",
                  }}
                >
                  <Button
                    color="inherit"
                    onClick={handleNavigation(PATH_NAME.SMART_FRIDGE)}
                    sx={{
                      ...HeaderStyles.button,
                      ...HeaderStyles.getActiveButtonStyle(
                        isActive(PATH_NAME.SMART_FRIDGE)
                      ),
                    }}
                  >
                    <Typography sx={{ ...typo.navigation }}>
                      Smart Fridge
                    </Typography>
                    <CustomSVG />
                  </Button>
                  
                  <Button
                    color="inherit"
                    onClick={handleNavigation(
                      basicUserInfo ? PATH_NAME.MEMBER : PATH_NAME.LOGIN
                    )}
                    sx={{
                      ...HeaderStyles.button,
                      ...HeaderStyles.getActiveButtonStyle(isActive(PATH_NAME.LOGIN)),
                    }}
                  >
                    <Typography sx={{ ...typo.navigation }}>
                      {basicUserInfo ? "My Account" : "Login"}
                    </Typography>
                    <CustomSVG />
                  </Button>
                  {isCartEnabled && (
                    <Button
                      onClick={handleNavigation(PATH_NAME.CART)}
                      sx={{
                        ...HeaderStyles.button,
                      }}
                    >
                      <Badge
                        badgeContent={totalQuantity}
                        color="primary"
                        overlap="circular"
                        sx={{
                          "& .MuiBadge-badge": {
                            backgroundColor: colorConfigs.primary,
                            color: "var(--color-white)",
                          },
                          color: "var(--color-black)",
                        }}
                      >
                        <ShoppingCartOutlinedIcon sx={{ fontSize: "2rem" }} />
                      </Badge>
                    </Button>
                  )}
                </Box>
              </>
            )}
          </Box>
        </Toolbar>
      </AppBar>

      {/* Submenu Header */}
      {isSubmenuVisible && !isMobile && !isTablet && (
        <Box
          onMouseEnter={showSubmenu}
          onMouseLeave={hideSubmenu}
          sx={{
            position: "absolute",
            top: appBarHeight,
            left: 0,
            width: "100%",
            zIndex: 1000,
          }}
        >
          <SubmenuHeader
            appBarHeight="auto"
            isMobile={isMobile}
            isTablet={isTablet}
          />
        </Box>
      )}
    </Box>
  );
};

export default MainHeader;